import { type GitRemote } from "../git/git.types";

export type WorkspaceDeleteTarget = {
	candidates: string[];
	required: boolean;
	source: "remote" | "git";
};

type WorkspaceRef = {
	id?: string;
	name?: string;
	slug?: string;
};

const normalizeIdentifier = (value: string): string => value.trim().toLowerCase();

const dedupeIdentifiers = (identifiers: (string | undefined)[]): string[] => {
	const unique = new Set<string>();
	const deduped: string[] = [];

	for (const identifier of identifiers) {
		if (!identifier) {
			continue;
		}
		const trimmed = identifier.trim();
		if (trimmed.length === 0) {
			continue;
		}

		const key = normalizeIdentifier(trimmed);
		if (unique.has(key)) {
			continue;
		}

		unique.add(key);
		deduped.push(trimmed);
	}

	return deduped;
};

/**
 * Parses a workspace identifier from a git remote URL.
 * Supports both URL-style remotes (`https://host/git/workspace/document.git`) and
 * scp-style remotes (`git@host:workspace/document.git`).
 */
export const parseWorkspaceFromRemoteUrl = (remoteUrl: string): string | undefined => {
	try {
		const parsed = new URL(remoteUrl);
		const segments = parsed.pathname.split("/").filter(Boolean);
		if (segments.length < 2) {
			return undefined;
		}

		return decodeURIComponent(segments[segments.length - 2]);
	} catch {
		// ignore and try scp-style parsing below
	}

	const scpLikeMatch = remoteUrl.match(/:(?<workspace>[^/:]+)\/[^/]+(?:\.git)?$/);
	return scpLikeMatch?.groups?.workspace;
};

const addTarget = (
	targets: WorkspaceDeleteTarget[],
	identifiers: (string | undefined)[],
	required: boolean,
	source: "remote" | "git"
): void => {
	const cleanedIdentifiers = dedupeIdentifiers(identifiers);
	if (cleanedIdentifiers.length === 0) {
		return;
	}

	const normalized = new Set(cleanedIdentifiers.map(normalizeIdentifier));
	let target = targets.find((candidate) =>
		candidate.candidates.some((candidateIdentifier) =>
			normalized.has(normalizeIdentifier(candidateIdentifier))
		)
	);

	if (!target) {
		target = {
			candidates: [],
			required,
			source,
		};
		targets.push(target);
	}

	target.required = target.required || required;
	if (target.source !== "remote" && source === "remote") {
		target.source = "remote";
	}

	target.candidates = dedupeIdentifiers([...target.candidates, ...cleanedIdentifiers]);
};

export const buildWorkspaceDeleteTargets = (
	remoteWorkspaces: WorkspaceRef[],
	gitRemotes: GitRemote[]
): WorkspaceDeleteTarget[] => {
	const targets: WorkspaceDeleteTarget[] = [];

	for (const workspace of remoteWorkspaces) {
		addTarget(targets, [workspace.name, workspace.slug, workspace.id], true, "remote");
	}

	for (const gitRemote of gitRemotes) {
		const workspaceFromRemote = parseWorkspaceFromRemoteUrl(gitRemote.url);
		addTarget(targets, [workspaceFromRemote], false, "git");
	}

	return targets;
};

export type DeleteTargetFailure = {
	target: WorkspaceDeleteTarget;
	errors: unknown[];
};

export const deleteRemoteDocumentFromTargets = async (
	targets: WorkspaceDeleteTarget[],
	deleteWorkspace: (workspaceIdentifier: string) => Promise<void>
): Promise<DeleteTargetFailure[]> => {
	const failures: DeleteTargetFailure[] = [];

	for (const target of targets) {
		const errors: unknown[] = [];
		let deleted = false;

		for (const workspaceIdentifier of target.candidates) {
			try {
				await deleteWorkspace(workspaceIdentifier);
				deleted = true;
				break;
			} catch (error) {
				errors.push(error);
			}
		}

		if (!deleted) {
			failures.push({ target, errors });
		}
	}

	return failures;
};
